import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import LockedPrompt from '@/components/feature/LockedPrompt';
import { forexApi, type TradePositionDTO, verifiedPnlText } from '@/services/forexApi';

function formatSymbol(value: string): string {
  return value.length === 6 ? `${value.slice(0, 3)}/${value.slice(3)}` : value;
}

export default function TradeHistoryPage() {
  const navigate = useNavigate();
  const { user, accessToken } = useAuth();
  const [items, setItems] = useState<TradePositionDTO[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [symbol, setSymbol] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user || !accessToken) return;
    let active = true;
    setLoading(true);
    setItems([]);
    setCursor(null);
    setError('');
    void forexApi.listClosedTradePositions(accessToken, null, symbol || undefined).then((page) => {
      if (!active) return;
      setItems(page.items);
      setCursor(page.next_cursor);
    }).catch(() => { if (active) setError('Broker 历史记录暂不可用，请重试'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [user, accessToken, symbol]);

  async function loadMore() {
    if (!accessToken || !cursor || loading) return;
    setLoading(true);
    setError('');
    try {
      const page = await forexApi.listClosedTradePositions(accessToken, cursor, symbol || undefined);
      setItems((current) => [...current, ...page.items]);
      setCursor(page.next_cursor);
    } catch {
      setError('后续记录加载失败，请重试');
    } finally {
      setLoading(false);
    }
  }

  if (!user) return <main className="min-h-screen bg-gray-50 px-5 pt-12">
    <h1 className="text-xl font-bold">历史交易记录</h1>
    <LockedPrompt title="历史交易记录已保护" subtitle="登录后查看本人 Broker 已平仓记录"
      buttonText="登录查看记录" onButtonClick={() => navigate('/auth', { state: { from: '/trade-history' } })} />
  </main>;

  return <main className="min-h-screen bg-gray-50 px-5 pb-24 pt-12">
    <button type="button" onClick={() => navigate('/assets')} className="text-sm text-indigo-600">返回资产</button>
    <h1 className="mt-4 text-xl font-bold text-gray-900">本人 Broker 已平仓记录</h1>
    <p className="mt-2 text-xs text-gray-500">仅统计本人账号已关联的 Broker 事实。待执行、EA 接收及待核对结果请在决策详情查看，不计入收益。</p>
    <div className="mt-4 flex gap-2">
      <button type="button" onClick={() => setSymbol('')} className="rounded-lg border bg-white px-3 py-2 text-xs">全部品种</button>
      <input aria-label="按品种筛选" value={symbol} onChange={(event) => setSymbol(event.target.value.toUpperCase())}
        placeholder="EURUSD" maxLength={12} className="min-w-0 rounded-lg border px-3 py-2 text-xs" />
    </div>
    {error && <p role="alert" className="mt-4 text-sm text-amber-700">{error}</p>}
    {loading && items.length === 0 && <p role="status" className="mt-4 text-sm text-gray-500">正在加载记录…</p>}
    <div className="mt-4 space-y-3">
      {items.map((item) => <article key={item.id} className="rounded-xl bg-white p-4 shadow-sm">
        <button type="button" className="w-full text-left" aria-expanded={selectedId === item.id}
          onClick={() => setSelectedId(selectedId === item.id ? null : item.id)}>
          <span className="font-semibold text-gray-900">{formatSymbol(item.symbol)} · {item.action}</span>
          <span className="ml-2 text-xs text-gray-500">{item.follower_account_masked || '历史子账号'}</span>
          <p className="mt-2 text-sm text-gray-700">{verifiedPnlText(item.pnl, item.pnl_currency)}</p>
          <p className="mt-1 text-xs text-gray-500">{item.opened_at || '成交时间待核对'} · {item.lot ?? '手数待核对'} lot</p>
        </button>
        {selectedId === item.id && <div className="mt-3 border-t pt-3 text-xs text-gray-600">
          <p>Broker 订单：{item.broker_ticket_masked}；开仓价：{item.open_price ?? '待核对'}</p>
          <p className="mt-1">保护状态：{item.protection_status || '待核对'}；时间核验：{item.time_status || '待核对'}</p>
          <p className="mt-1">平仓时间：{item.closed_at || '待核对'}；同步状态：{item.sync_status || '待核对'}</p>
          <p className="mt-1">关闭来源：{item.close_source === 'user_position_close' ? '本人主动平仓' : '待核对'}{item.close_request_id ? `；请求编号：${item.close_request_id.slice(-8)}` : ''}</p>
        </div>}
      </article>)}
    </div>
    {!loading && !error && items.length === 0 && <p className="mt-6 text-sm text-gray-500">暂无本人已平仓 Broker 记录。</p>}
    {cursor && <button type="button" disabled={loading} onClick={() => void loadMore()} className="mt-5 rounded-lg border px-4 py-2 text-sm disabled:opacity-50">加载更多</button>}
  </main>;
}
